import { useState } from "react";
import { SEOHead } from "@/components/SEOHead";
import { Layout } from "@/components/layout/Layout";
import { Link } from "react-router-dom";
import { Bot, Zap, Shield, BarChart3, Clock, Users, CheckCircle, ArrowRight, Brain, Target, Workflow, TrendingUp, Database, RefreshCw, Lock, Globe, Layers, Search, FileText, Mail, Phone, MessageSquare, Calendar, Building, DollarSign, AlertTriangle, Star, Quote, Calculator, Eye, Cpu, Sparkles, Mic, Headphones } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";

const CRMAgent = () => {
  const [teamSize, setTeamSize] = useState(10);
  const [avgDeal, setAvgDeal] = useState(25000);
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "NoQ5 CRM Agent",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web",
      "description": "Autonomous CRM agent that updates Salesforce, HubSpot and Pipedrive from calls, emails and meetings. Enriches contacts, qualifies leads, and generates pipeline insights without manual data entry.",
      "url": "https://noq5.ai/crm-agent",
      "offers": {
        "@type": "Offer",
        "price": "59",
        "priceCurrency": "EUR",
        "description": "€59 per user per month, billed annually"
      },
      "publisher": {
        "@type": "Organization",
        "name": "NoQ5",
        "url": "https://noq5.ai"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is a CRM agent?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "A CRM agent is an AI-powered autonomous software that manages your Customer Relationship Management system without human intervention. Unlike traditional CRM tools that require manual data entry, a CRM agent automatically logs activities, updates deal stages, enriches contact data, qualifies leads, and generates pipeline insights — acting as an intelligent chief of staff for your sales team."
          }
        },
        {
          "@type": "Question",
          "name": "What is a CRM AI agent?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "A CRM AI agent is an artificial intelligence system that autonomously operates within your CRM (Salesforce, HubSpot, Pipedrive) to automate repetitive tasks, analyze sales data, predict deal outcomes, and provide actionable recommendations. It uses natural language processing, machine learning, and agentic AI to understand sales conversations and take action without manual input."
          }
        },
        {
          "@type": "Question",
          "name": "How does a CRM agent differ from CRM automation?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Traditional CRM automation follows rigid if-then rules. A CRM agent uses AI to make contextual decisions autonomously — it understands meeting transcripts, interprets email sentiment, identifies buying signals, and determines the right action without pre-programmed rules. It adapts and learns from your sales process."
          }
        },
        {
          "@type": "Question",
          "name": "Which CRMs does the NoQ5 CRM AI agent work with?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "NoQ5's CRM AI agent integrates natively with Salesforce, HubSpot, and Pipedrive. It also connects with Google Workspace, Microsoft 365, Slack, Zoom, and LinkedIn to capture activities across all your sales channels and sync them to your CRM automatically."
          }
        },
        {
          "@type": "Question",
          "name": "Is a CRM AI agent GDPR compliant?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. NoQ5's CRM AI agent is fully GDPR and SOC 2 compliant. All data is processed in EU data centers, with end-to-end encryption, automatic data retention policies, and consent management built in. No customer data is used for model training."
          }
        },
        {
          "@type": "Question",
          "name": "How much time does a CRM agent save sales reps?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Sales reps spend an average of 5.5 hours per week on manual CRM updates. A CRM AI agent eliminates 90%+ of this administrative burden, saving each rep approximately 5 hours per week — that's 250+ hours per year per rep redirected to selling activities."
          }
        },
        {
          "@type": "Question",
          "name": "How does NoQ5 compare to Salesforce Agentforce?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Salesforce Agentforce is limited to the Salesforce ecosystem and requires extensive configuration. NoQ5's CRM agent works across Salesforce, HubSpot, and Pipedrive with zero-config setup, captures activities from all channels (email, calls, Slack, LinkedIn), and provides cross-platform intelligence that Agentforce cannot match."
          }
        },
        {
          "@type": "Question",
          "name": "What CRM fields does the AI agent update automatically?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "NoQ5's CRM agent updates 40+ field types including: deal stage, amount, close date, next steps, last activity date, contact title, company size, decision maker status, competitor mentions, MEDDIC/BANT qualification fields, meeting notes, call summaries, email engagement scores, and custom fields specific to your sales process."
          }
        },
        {
          "@type": "Question",
          "name": "What is a CRM enrichment agent?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "A CRM enrichment agent is a specialized AI agent that automatically fills in missing contact and company data in your CRM — including job titles, company size, industry, tech stack, funding status, and social profiles — by aggregating data from multiple sources in real-time, without manual research."
          }
        },
        {
          "@type": "Question",
          "name": "Can a CRM agent work with custom CRM fields?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. NoQ5's CRM agent maps to your custom fields, picklist values, and sales process stages. During setup, it learns your CRM schema and adapts its field mapping accordingly — no developer resources required."
          }
        },
        {
          "@type": "Question",
          "name": "What is a voice CRM?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "A voice CRM is a customer relationship management system that captures and structures data from spoken sales conversations — calls, meetings, voice notes — and writes the result directly into CRM fields, without a rep typing anything. NoQ5's CRM agent is voice-native: it ingests every call recording, transcribes, extracts deal-relevant fields, and updates Salesforce, HubSpot or Pipedrive autonomously."
          }
        },
        {
          "@type": "Question",
          "name": "Which companies offer the best voice AI for CRM?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Most voice AI tools stop at transcription or summary. NoQ5 is the only autonomous CRM that takes voice from a call into structured CRM updates, lead scoring and forecast signals without a rep in the loop. Standalone voice CRM startups (CRMVO, VoiceCRM.app) cover capture; voice agent platforms (ElevenLabs, Goodcall) cover synthesis. NoQ5 covers the full loop: voice in, CRM updated, pipeline re-scored, brief written."
          }
        },
        {
          "@type": "Question",
          "name": "How does voice AI integrate with my CRM?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "NoQ5 connects to your call platform (Zoom, Google Meet, Microsoft Teams, dialer of choice) and to your CRM via OAuth. Every call is transcribed, mapped to the right contact and deal, parsed for MEDDIC, BANT, competitor mentions and next steps, then written to the correct fields. The rep sees the updates on the deal record, the CRO sees them in the forecast."
          }
        }
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://noq5.ai" },
        { "@type": "ListItem", "position": 2, "name": "CRM Agent", "item": "https://noq5.ai/crm-agent" }
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "CRM Agent — AI-Powered CRM Automation | NoQ5",
      "description": "The #1 CRM Agent and CRM AI Agent for sales teams. Automate CRM updates, enrich contacts, qualify leads, and predict revenue with autonomous AI.",
      "url": "https://noq5.ai/crm-agent",
      "isPartOf": { "@type": "WebSite", "url": "https://noq5.ai" },
      "about": [
        { "@type": "Thing", "name": "CRM Agent" },
        { "@type": "Thing", "name": "CRM AI Agent" },
        { "@type": "Thing", "name": "AI CRM Automation" },
        { "@type": "Thing", "name": "Autonomous CRM" },
        { "@type": "Thing", "name": "Agentic CRM" },
        { "@type": "Thing", "name": "CRM Enrichment Agent" },
        { "@type": "Thing", "name": "CRM Data Entry Automation" }
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": "How to Deploy a CRM AI Agent",
      "description": "Step-by-step guide to implementing an autonomous CRM agent that eliminates manual data entry and automates pipeline management.",
      "step": [
        { "@type": "HowToStep", "name": "Connect Your CRM", "text": "Link your Salesforce, HubSpot, or Pipedrive account using OAuth. NoQ5 reads your CRM schema, custom fields, and pipeline stages automatically." },
        { "@type": "HowToStep", "name": "Connect Communication Channels", "text": "Connect Gmail, Outlook, Zoom, Google Meet, Slack, and LinkedIn. The CRM agent begins capturing all sales activities across channels." },
        { "@type": "HowToStep", "name": "Configure Field Mapping", "text": "Review the auto-detected field mappings. Customize which CRM fields the agent updates, add custom field rules, and set approval workflows if needed." },
        { "@type": "HowToStep", "name": "Activate Autonomous Mode", "text": "Enable the CRM agent. It begins processing meetings, emails, and messages — updating your CRM in real-time with zero manual input." }
      ],
      "totalTime": "PT15M"
    }
  ];

  const features = [
    { icon: Bot, title: "Autonomous CRM Updates", desc: "Your CRM AI agent listens to calls, reads emails, and updates every field — contacts, deals, notes, next steps — without any manual input. Supports 40+ field types including custom fields." },
    { icon: Brain, title: "Intelligent Lead Qualification", desc: "The CRM agent scores and qualifies leads using buying signals from conversations, email engagement, and behavioral data. Supports MEDDIC, BANT, and custom qualification frameworks." },
    { icon: BarChart3, title: "AI-Powered Pipeline Intelligence", desc: "Get real-time deal health scores, win probability predictions, and pipeline risk alerts generated autonomously by your CRM AI agent — with 35% greater accuracy than manual forecasting." },
    { icon: Database, title: "CRM Data Enrichment", desc: "Automatically enrich contacts with job titles, company size, tech stack, funding status, and 30+ firmographic fields — aggregated from multiple sources without manual research." },
    { icon: Workflow, title: "Cross-Platform Activity Capture", desc: "Your CRM agent captures activities from Salesforce, HubSpot, Slack, Zoom, Gmail, LinkedIn, and Microsoft Teams — syncing everything to one source of truth." },
    { icon: Shield, title: "Enterprise-Grade Security", desc: "SOC 2 Type II certified, GDPR compliant, EU data residency. Your CRM AI agent processes data with zero-trust architecture and end-to-end encryption. No data used for model training." },
  ];

  const crmFieldsUpdated = [
    { category: "Deal Fields", fields: ["Deal Stage", "Amount", "Close Date", "Probability", "Next Steps", "Forecast Category", "Competitor", "Loss Reason"] },
    { category: "Contact Fields", fields: ["Title", "Department", "Decision Maker", "Last Activity", "Engagement Score", "LinkedIn URL", "Phone", "Email Verified"] },
    { category: "Activity Fields", fields: ["Call Notes", "Meeting Summary", "Email Thread", "Follow-up Tasks", "Action Items", "Objections Raised", "Buying Signals", "Champion Status"] },
    { category: "Intelligence Fields", fields: ["MEDDIC Score", "BANT Qualification", "Risk Level", "Sentiment Trend", "Multi-threading Score", "Competitive Displacement Risk", "Budget Confirmed", "Timeline Identified"] },
  ];

  const beforeAfter = [
    { before: "Reps spend 5.5 hours/week manually updating CRM fields after every call and email", after: "CRM agent auto-updates 40+ fields in real-time — reps never touch a CRM form" },
    { before: "30-50% of CRM records have missing or outdated contact information", after: "AI enrichment agent fills missing fields from 15+ data sources with 95% accuracy" },
    { before: "Sales managers chase reps weekly for deal updates and forecast inputs", after: "CRM agent generates evidence-based forecasts automatically from actual engagement data" },
    { before: "Deals go dark for 2-3 weeks before anyone notices the lack of buyer engagement", after: "Proactive alerts trigger within 48 hours of declining engagement or missed follow-ups" },
    { before: "New reps take 3-6 months to learn CRM hygiene standards and process", after: "CRM agent enforces data quality from day one — zero ramp time for CRM compliance" },
    { before: "Quarter-end scramble to reconcile pipeline data for board reporting", after: "Real-time, always-accurate pipeline data available on demand in any format" },
  ];

  const useCases = [
    { title: "CRM Agent for Salesforce", desc: "Automate Salesforce updates after every meeting. Log calls, update opportunities, create follow-up tasks, and sync MEDDIC fields — all handled by your AI CRM agent.", link: "/ai-agent-salesforce" },
    { title: "CRM Agent for HubSpot", desc: "Sync HubSpot deals with real-time conversation intelligence. Your CRM AI agent enriches contacts, updates deal stages, triggers workflows, and keeps properties current.", link: "/integrations/hubspot" },
    { title: "CRM Agent for Pipedrive", desc: "Keep Pipedrive clean and current. The CRM agent captures every interaction and maps it to the right deal, contact, and pipeline stage automatically.", link: "/integrations/pipedrive" },
    { title: "CRM Agent for Revenue Teams", desc: "Give CROs and VPs a complete view of pipeline health with AI-generated forecasts, risk scores, deal velocity metrics, and coaching insights.", link: "/ai-for-cros" },
    { title: "CRM Enrichment Agent", desc: "Automatically fill missing contact and company data — job titles, tech stack, funding, company size — from 15+ sources without manual research.", link: "/crm-enrichment-agent" },
    { title: "CRM Data Entry Automation", desc: "Eliminate 100% of manual CRM data entry. The AI agent logs every call, email, and meeting to the right record with the right fields.", link: "/crm-data-entry-automation" },
  ];

  const stats = [
    { value: "90%", label: "Less Manual CRM Data Entry" },
    { value: "5h", label: "Saved Per Rep Per Week" },
    { value: "40+", label: "CRM Fields Auto-Updated" },
    { value: "15min", label: "Setup to First CRM Update" },
  ];

  const competitors = [
    { name: "Manual CRM Entry", auto: "❌", enrichment: "❌", voice: "❌", forecast: "❌", multiCRM: "❌", security: "⚠️" },
    { name: "Salesforce Agentforce", auto: "⚠️", enrichment: "⚠️", voice: "❌", forecast: "⚠️", multiCRM: "❌", security: "✅" },
    { name: "HubSpot Breeze AI", auto: "⚠️", enrichment: "⚠️", voice: "❌", forecast: "⚠️", multiCRM: "❌", security: "✅" },
    { name: "Relevance AI", auto: "⚠️", enrichment: "✅", voice: "❌", forecast: "❌", multiCRM: "⚠️", security: "⚠️" },
    { name: "Apollo / ZoomInfo", auto: "❌", enrichment: "✅", voice: "❌", forecast: "❌", multiCRM: "⚠️", security: "⚠️" },
    { name: "NoQ5 CRM Agent", auto: "✅", enrichment: "✅", voice: "✅", forecast: "✅", multiCRM: "✅", security: "✅" },
  ];

  const industries = [
    { name: "SaaS & Software", desc: "Complex sales cycles with multiple stakeholders. The CRM agent tracks multi-threaded deals, maps buying committees, and updates MEDDIC scores from conversation context.", icon: Layers },
    { name: "Financial Services", desc: "Compliance-heavy environments where audit trails matter. Every CRM update is logged, encrypted, and GDPR/MiFID II compliant with full data lineage.", icon: DollarSign },
    { name: "Healthcare & MedTech", desc: "Long procurement cycles with regulatory requirements. The CRM agent tracks committee decisions, compliance checkpoints, and multi-stage approval workflows.", icon: Shield },
    { name: "Manufacturing & Industrial", desc: "Complex quoting and multi-location deals. Track distributor relationships, pricing tiers, and installation timelines across global CRM instances.", icon: Building },
  ];

  const implementationTimeline = [
    { time: "Day 1", title: "Connect & Configure", desc: "OAuth connection to your CRM + communication tools. Auto-detection of your CRM schema, custom fields, and pipeline stages. Zero developer resources needed." },
    { time: "Day 2-3", title: "Learning Phase", desc: "The CRM agent processes your historical data to learn field patterns, naming conventions, and sales process. It maps activities to contacts, companies, and deals." },
    { time: "Day 4-5", title: "Supervised Mode", desc: "CRM agent operates in review mode — it proposes updates and you approve. This trains the agent on your specific preferences and edge cases." },
    { time: "Week 2", title: "Autonomous Mode", desc: "Full autonomous operation. The CRM agent handles all updates, enrichment, and intelligence generation without manual intervention. 90% reduction in CRM admin time." },
  ];

  return (
    <Layout>
      <SEOHead
        title="CRM Agent: The Autonomous CRM Agent for Salesforce, HubSpot & Pipedrive | NoQ5"
        description="NoQ5 is the CRM Agent that updates your CRM from calls, emails and meetings. Enriches contacts, qualifies leads, scores deals, and saves reps 5+ hours a week. Works with Salesforce, HubSpot and Pipedrive."
        keywords="CRM agent, AI agent for CRM, AI agents for CRM, CRM AI agent, autonomous CRM, agentic CRM, CRM automation agent, AI CRM agent, salesforce AI agent, hubspot AI agent, pipedrive AI agent, CRM enrichment agent, CRM data entry agent, AI sales agent, revenue intelligence agent"
        canonicalUrl="https://noq5.ai/crm-agent"
        jsonLd={jsonLd}
      />

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-primary/5" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
              <Bot className="h-4 w-4" />
              <span>Autonomous CRM Agent for Revenue Teams</span>
            </div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-foreground mb-6 leading-tight">
              The <span className="text-primary">CRM Agent</span> That Runs Your CRM For You
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-4 max-w-3xl mx-auto">
              A <strong>CRM agent</strong> is an AI that operates your CRM on its own. NoQ5 listens to your calls, reads your emails, enriches your contacts, and updates Salesforce, HubSpot or Pipedrive without anyone touching a field.
            </p>
            <p className="text-base text-muted-foreground mb-8 max-w-2xl mx-auto">
              Your revenue team's <strong>AI Chief of Staff</strong>. 15-minute setup. 40+ fields updated automatically. No rules, no macros, no code.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/trial">
                <Button size="lg" className="w-full sm:w-auto text-lg px-8 py-6 bg-primary hover:bg-primary/90 text-primary-foreground">
                  Deploy Your CRM Agent <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/product/platform">
                <Button size="lg" variant="outline" className="w-full sm:w-auto text-lg px-8 py-6 border-border text-foreground hover:bg-accent">
                  See How It Works
                </Button>
              </Link>
            </div>
            {/* Trust signals */}
            <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
              <span className="flex items-center gap-1"><Shield className="h-4 w-4 text-primary" /> SOC 2 Certified</span>
              <span className="flex items-center gap-1"><Globe className="h-4 w-4 text-primary" /> GDPR Compliant</span>
              <span className="flex items-center gap-1"><Lock className="h-4 w-4 text-primary" /> EU Data Residency</span>
              <span className="flex items-center gap-1"><Zap className="h-4 w-4 text-primary" /> 15-Min Setup</span>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-muted/30 border-y border-border">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-primary mb-1">{stat.value}</div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof — Testimonials */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 text-center">
            What Revenue Teams Say About Their CRM Agent
          </h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">Teams across SaaS, fintech, and professional services use NoQ5 to eliminate CRM admin work.</p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { quote: "I used to spend 45 minutes after every call updating Salesforce. Now it's done before I hang up. I've reclaimed my entire Friday afternoon.", name: "Sarah K.", role: "Senior AE, Series B SaaS", stars: 5 },
              { quote: "We finally have pipeline data we can trust for board meetings. The AI forecasts have been within 8% of actual every quarter since we deployed.", name: "Marcus R.", role: "VP Revenue, Fintech Scale-up", stars: 5 },
              { quote: "Our CRM completeness went from 43% to 97% in 3 weeks. RevOps finally stopped being the CRM police and started doing strategic work.", name: "Priya D.", role: "Head of RevOps, Mid-Market SaaS", stars: 5 },
            ].map((t, i) => (
              <div key={i} className="bg-card border border-border rounded-xl p-6 flex flex-col">
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: t.stars }).map((_, j) => (
                    <Star key={j} className="h-4 w-4 fill-primary text-primary" />
                  ))}
                </div>
                <Quote className="h-6 w-6 text-primary/30 mb-2" />
                <p className="text-sm text-muted-foreground italic flex-1 mb-4">"{t.quote}"</p>
                <div>
                  <p className="text-sm font-semibold text-foreground">{t.name}</p>
                  <p className="text-xs text-muted-foreground">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 text-center">
            Before vs. After: Life With a CRM AI Agent
          </h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">See exactly how an autonomous CRM agent transforms your sales operations — from manual chaos to AI-powered precision.</p>
          <div className="space-y-4">
            {beforeAfter.map((item, i) => (
              <div key={i} className="grid md:grid-cols-2 gap-4">
                <div className="bg-destructive/5 border border-destructive/20 rounded-xl p-5 flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-xs font-semibold text-destructive uppercase mb-1">Without CRM Agent</div>
                    <p className="text-sm text-foreground">{item.before}</p>
                  </div>
                </div>
                <div className="bg-primary/5 border border-primary/20 rounded-xl p-5 flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-xs font-semibold text-primary uppercase mb-1">With NoQ5 CRM Agent</div>
                    <p className="text-sm text-foreground">{item.after}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mini ROI Calculator — Interactive engagement */}
      <section className="py-16 md:py-24 bg-primary/5">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 text-center">
            <Calculator className="inline h-8 w-8 text-primary mr-2" />
            Calculate Your CRM Agent ROI
          </h2>
          <p className="text-center text-muted-foreground mb-10">See how much time and revenue your team recovers with an autonomous CRM agent.</p>
          <div className="bg-card border border-border rounded-2xl p-6 md:p-8">
            <div className="space-y-8">
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Sales team size: <span className="text-primary font-bold">{teamSize} reps</span></label>
                <Slider value={[teamSize]} onValueChange={(v) => setTeamSize(v[0])} min={1} max={100} step={1} className="mt-2" />
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Average deal size: <span className="text-primary font-bold">${avgDeal.toLocaleString()}</span></label>
                <Slider value={[avgDeal]} onValueChange={(v) => setAvgDeal(v[0])} min={5000} max={500000} step={5000} className="mt-2" />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-border">
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-primary">{(teamSize * 5).toLocaleString()}h</div>
                  <div className="text-xs text-muted-foreground mt-1">Hours saved / week</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-primary">{(teamSize * 250).toLocaleString()}h</div>
                  <div className="text-xs text-muted-foreground mt-1">Hours saved / year</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-primary">${Math.round(teamSize * avgDeal * 0.15).toLocaleString()}</div>
                  <div className="text-xs text-muted-foreground mt-1">Pipeline uplift / quarter</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-primary">{Math.round(teamSize * 250 * 75).toLocaleString()}$</div>
                  <div className="text-xs text-muted-foreground mt-1">Cost savings / year</div>
                </div>
              </div>
            </div>
            <div className="mt-6 text-center">
              <Link to="/trial">
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground">
                  Get These Results <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* The NoQ5 Approach — Unique Methodology */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 text-center">
            The NoQ5 Approach: Why Our CRM Agent Is Different
          </h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">Most "CRM agents" are repackaged automation. NoQ5 uses a 3-layer architecture that no competitor replicates.</p>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-card border-2 border-primary/20 rounded-2xl p-6 text-center relative">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-full">Layer 1</div>
              <Eye className="h-10 w-10 text-primary mx-auto mb-4 mt-2" />
              <h3 className="text-xl font-bold text-foreground mb-2">Capture</h3>
              <p className="text-sm text-muted-foreground">Monitors every channel — calls, emails, Slack, LinkedIn, calendar — and ingests raw interaction data in real-time. No manual tagging. No missed activities.</p>
            </div>
            <div className="bg-card border-2 border-primary/30 rounded-2xl p-6 text-center relative">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-full">Layer 2</div>
              <Cpu className="h-10 w-10 text-primary mx-auto mb-4 mt-2" />
              <h3 className="text-xl font-bold text-foreground mb-2">Understand</h3>
              <p className="text-sm text-muted-foreground">LLMs extract entities, sentiment, buying signals, objections, and next steps. Maps each data point to the correct contact, deal, and field in YOUR CRM schema.</p>
            </div>
            <div className="bg-card border-2 border-primary/40 rounded-2xl p-6 text-center relative">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-full">Layer 3</div>
              <Sparkles className="h-10 w-10 text-primary mx-auto mb-4 mt-2" />
              <h3 className="text-xl font-bold text-foreground mb-2">Act</h3>
              <p className="text-sm text-muted-foreground">Writes to your CRM with confidence scoring. High-confidence updates auto-apply; edge cases get flagged. Generates forecasts, risk alerts, and coaching signals.</p>
            </div>
          </div>
          <p className="text-center text-sm text-muted-foreground mt-8 max-w-2xl mx-auto">
            <strong className="text-foreground">Why this matters:</strong> Salesforce Agentforce only does Layer 3 (act) — it needs you to provide the data. Relevance AI builds tools for Layer 2 (understand) — but doesn't capture or write natively. <strong className="text-foreground">NoQ5 is the only CRM agent that owns all three layers end-to-end.</strong>
          </p>
        </div>
      </section>

      {/* What It Feels Like — Rep Experience */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 text-center">
            What It Actually Feels Like
          </h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            We don't sell features. We sell the feeling of never fighting your CRM again.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { text: "I don't manually update CRM anymore", icon: RefreshCw },
              { text: "I don't hunt for context before calls", icon: Search },
              { text: "I don't forget next steps", icon: CheckCircle },
              { text: "I don't waste time switching tabs", icon: Layers },
              { text: "I trust what this assistant writes", icon: Shield },
              { text: "I can ask anything about my pipeline", icon: MessageSquare },
              { text: "I can act immediately from the answer", icon: Zap },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-4 p-4 rounded-xl bg-card border border-border hover:border-primary/30 transition-colors">
                <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <item.icon className="h-5 w-5 text-primary" />
                </div>
                <span className="text-foreground font-medium">{item.text}</span>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-muted-foreground mt-8 italic">
            That's the magic. Not another dashboard. Not another tab. Just a rep who finally has time to sell.
          </p>
        </div>
      </section>

      {/* First 30 Days Results — Data-driven proof */}
      <section className="py-16 md:py-24 bg-muted/20">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 text-center">
            What Happens in Your First 30 Days With a CRM Agent
          </h2>
          <p className="text-center text-muted-foreground mb-12">Real results from teams who deployed NoQ5. Not projections — actuals.</p>
          <div className="space-y-6">
            {[
              { week: "Week 1", metric: "CRM field completeness jumps from ~45% to 78%", detail: "The agent processes historical emails and calendar data to backfill missing fields. Most teams see 500+ CRM updates in the first 72 hours.", color: "bg-primary/10 border-primary/20" },
              { week: "Week 2", metric: "Manual CRM time drops by 80%", detail: "Reps stop logging calls and updating deals. The supervised mode catches edge cases while the agent learns your team's specific conventions.", color: "bg-primary/20 border-primary/30" },
              { week: "Week 3", metric: "Pipeline forecast accuracy improves by 25%", detail: "With complete activity data, the AI generates forecasts based on actual engagement — not gut feel. Managers start using AI-generated deal briefs for 1:1s.", color: "bg-primary/30 border-primary/40" },
              { week: "Week 4", metric: "CRM completeness exceeds 95%. Full ROI realized.", detail: "Autonomous mode is fully active. At-risk deals get flagged within 48 hours. RevOps shifts from data cleanup to strategic analysis. Average team saves 250+ hours/quarter.", color: "bg-primary/40 border-primary/50" },
            ].map((item, i) => (
              <div key={i} className={`rounded-xl border p-6 ${item.color}`}>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 px-3 py-1 rounded-lg bg-primary text-primary-foreground text-sm font-bold">{item.week}</div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-1">{item.metric}</h3>
                    <p className="text-sm text-muted-foreground">{item.detail}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What is a CRM Agent — Definitional SEO content */}
      <section className="py-16 md:py-24 bg-muted/20">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-8 text-center">
            What Is a CRM Agent?
          </h2>
          <div className="prose prose-lg max-w-none text-muted-foreground space-y-6">
            <p>
              A <strong>CRM agent</strong> is an AI-powered autonomous system that manages your Customer Relationship Management platform without human intervention. Unlike traditional CRM tools that rely on sales reps to manually log calls, update deal stages, and enter notes, a <strong>CRM AI agent</strong> uses artificial intelligence to perform these tasks automatically — with greater accuracy and zero lag time.
            </p>
            <p>
              Think of a CRM agent as your sales team's digital chief of staff. It listens to meetings, reads emails, analyzes engagement patterns, and takes action in your CRM in real-time. Whether you use Salesforce, HubSpot, or Pipedrive, an intelligent CRM agent ensures your data is always up-to-date, your pipeline is accurately forecasted, and your reps spend their time selling — not typing.
            </p>

            <h3 className="text-2xl font-bold text-foreground mt-8">CRM Agent vs. CRM Automation vs. CRM Copilot: Key Differences</h3>
            <div className="overflow-x-auto my-6">
              <table className="w-full border-collapse bg-card rounded-xl overflow-hidden border border-border text-sm">
                <thead>
                  <tr className="bg-muted">
                    <th className="text-left p-3 font-semibold text-foreground">Capability</th>
                    <th className="p-3 font-semibold text-foreground text-center">CRM Automation</th>
                    <th className="p-3 font-semibold text-foreground text-center">CRM Copilot</th>
                    <th className="p-3 font-semibold text-foreground text-center">CRM AI Agent</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr className="border-t border-border"><td className="p-3">Decision Making</td><td className="p-3 text-center">Rule-based</td><td className="p-3 text-center">Suggests actions</td><td className="p-3 text-center font-semibold text-foreground">Acts autonomously</td></tr>
                  <tr className="border-t border-border"><td className="p-3">Context Understanding</td><td className="p-3 text-center">None</td><td className="p-3 text-center">Partial</td><td className="p-3 text-center font-semibold text-foreground">Full conversation context</td></tr>
                  <tr className="border-t border-border"><td className="p-3">Setup Complexity</td><td className="p-3 text-center">High (rules)</td><td className="p-3 text-center">Medium</td><td className="p-3 text-center font-semibold text-foreground">Low (learns process)</td></tr>
                  <tr className="border-t border-border"><td className="p-3">Handles Edge Cases</td><td className="p-3 text-center">❌</td><td className="p-3 text-center">⚠️</td><td className="p-3 text-center font-semibold text-foreground">✅</td></tr>
                  <tr className="border-t border-border"><td className="p-3">Cross-Platform</td><td className="p-3 text-center">❌</td><td className="p-3 text-center">⚠️</td><td className="p-3 text-center font-semibold text-foreground">✅</td></tr>
                  <tr className="border-t border-border"><td className="p-3">Learns Over Time</td><td className="p-3 text-center">❌</td><td className="p-3 text-center">❌</td><td className="p-3 text-center font-semibold text-foreground">✅</td></tr>
                </tbody>
              </table>
            </div>

            <h3 className="text-2xl font-bold text-foreground mt-8">Why Sales Teams Need a CRM AI Agent in 2026</h3>
            <p>
              Research shows sales reps spend only 28% of their time actually selling. The rest goes to administrative tasks, with <strong>CRM data entry</strong> being the biggest time sink at 5.5 hours per week per rep. A <strong>CRM AI agent</strong> eliminates this burden entirely:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Automatic activity logging</strong> — every call, email, meeting, and Slack message captured and mapped to CRM records</li>
              <li><strong>Contextual deal updates</strong> — deal stages, amounts, and close dates adjusted based on conversation analysis, not manual input</li>
              <li><strong>Contact enrichment</strong> — job titles, company data, tech stack, and firmographics auto-populated from 15+ sources</li>
              <li><strong>AI-powered forecasting</strong> — evidence-based predictions with 35% greater accuracy than rep-submitted data</li>
              <li><strong>Follow-up generation</strong> — next steps and task recommendations extracted from meeting context</li>
              <li><strong>Risk detection</strong> — proactive alerts when deals show declining engagement, champion changes, or competitive threats</li>
              <li><strong>Coaching signals</strong> — conversation analysis surfaces skill gaps and winning patterns across the team</li>
            </ul>
          </div>
        </div>
      </section>

      {/* CRM Fields Updated — Proof Depth */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 text-center">
            40+ CRM Fields Updated Automatically
          </h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">Every field your sales process needs — updated by AI, not by reps. Here's exactly what the CRM agent handles:</p>
          <div className="grid md:grid-cols-2 gap-6">
            {crmFieldsUpdated.map((cat) => (
              <div key={cat.category} className="bg-card border border-border rounded-xl p-6">
                <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Database className="h-5 w-5 text-primary" />
                  {cat.category}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {cat.fields.map((field) => (
                    <span key={field} className="px-3 py-1 bg-primary/10 text-primary text-xs rounded-full font-medium">{field}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-muted-foreground mt-6">
            Plus full support for <strong>custom fields</strong>, <strong>picklist values</strong>, and <strong>multi-select fields</strong> specific to your sales process.
          </p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 md:py-24 bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              What Our CRM AI Agent Does
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Six core capabilities that make NoQ5 the most advanced <strong>CRM agent</strong> on the market — and why teams choose it over Salesforce Agentforce, HubSpot Breeze, and point solutions.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature) => (
              <div key={feature.title} className="bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-shadow">
                <feature.icon className="h-10 w-10 text-primary mb-4" />
                <h3 className="text-xl font-semibold text-foreground mb-2">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works + Implementation Timeline */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 text-center">
            How to Deploy a CRM AI Agent — Implementation Timeline
          </h2>
          <p className="text-center text-muted-foreground mb-12">From sign-up to autonomous CRM updates in under 2 weeks. No developers needed.</p>
          <div className="space-y-8">
            {implementationTimeline.map((item) => (
              <div key={item.time} className="flex gap-6 items-start">
                <div className="flex-shrink-0 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-bold min-w-[80px] text-center">
                  {item.time}
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">{item.title}</h3>
                  <p className="text-muted-foreground">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 md:py-24 bg-muted/20">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 text-center">
            CRM Agent Comparison: NoQ5 vs. Every Alternative
          </h2>
          <p className="text-center text-muted-foreground mb-8">How does the NoQ5 CRM AI agent stack up against Salesforce Agentforce, HubSpot Breeze, Relevance AI, and manual CRM entry?</p>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse bg-card rounded-xl overflow-hidden border border-border text-sm">
              <thead>
                <tr className="bg-muted">
                  <th className="text-left p-3 font-semibold text-foreground">CRM Agent Solution</th>
                  <th className="p-3 font-semibold text-foreground text-center">Auto Updates</th>
                  <th className="p-3 font-semibold text-foreground text-center">Enrichment</th>
                  <th className="p-3 font-semibold text-foreground text-center">Voice/Meeting AI</th>
                  <th className="p-3 font-semibold text-foreground text-center">Forecasting</th>
                  <th className="p-3 font-semibold text-foreground text-center">Multi-CRM</th>
                  <th className="p-3 font-semibold text-foreground text-center">Security</th>
                </tr>
              </thead>
              <tbody>
                {competitors.map((c, i) => (
                  <tr key={c.name} className={`border-t border-border ${i === competitors.length - 1 ? 'bg-primary/5 font-semibold' : ''}`}>
                    <td className="p-3 text-foreground">{c.name}</td>
                    <td className="p-3 text-center">{c.auto}</td>
                    <td className="p-3 text-center">{c.enrichment}</td>
                    <td className="p-3 text-center">{c.voice}</td>
                    <td className="p-3 text-center">{c.forecast}</td>
                    <td className="p-3 text-center">{c.multiCRM}</td>
                    <td className="p-3 text-center">{c.security}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-8 grid md:grid-cols-3 gap-6">
            <div className="bg-card border border-border rounded-lg p-5">
              <h4 className="font-semibold text-foreground mb-2">vs. Salesforce Agentforce</h4>
              <p className="text-sm text-muted-foreground">Agentforce only works within Salesforce. NoQ5 captures activities from every channel and works across Salesforce, HubSpot, and Pipedrive. <Link to="/comparison/salesforce-einstein" className="text-primary hover:underline">Full comparison →</Link></p>
            </div>
            <div className="bg-card border border-border rounded-lg p-5">
              <h4 className="font-semibold text-foreground mb-2">vs. HubSpot Breeze</h4>
              <p className="text-sm text-muted-foreground">Breeze is limited to HubSpot's ecosystem. NoQ5's CRM agent provides deeper conversation intelligence, cross-platform activity capture, and custom field support. <Link to="/comparison/hubspot" className="text-primary hover:underline">Full comparison →</Link></p>
            </div>
            <div className="bg-card border border-border rounded-lg p-5">
              <h4 className="font-semibold text-foreground mb-2">vs. Enrichment-Only Tools</h4>
              <p className="text-sm text-muted-foreground">Apollo and ZoomInfo enrich contacts but don't update deals, log activities, or forecast pipeline. NoQ5 does all of it from a single agent. <Link to="/crm-enrichment-agent" className="text-primary hover:underline">CRM enrichment details →</Link></p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases by Platform */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-12 text-center">
            CRM Agent Use Cases
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {useCases.map((uc) => (
              <Link key={uc.title} to={uc.link} className="group">
                <div className="bg-card border border-border rounded-xl p-6 hover:border-primary/50 transition-colors h-full">
                  <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">{uc.title}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{uc.desc}</p>
                  <span className="text-primary text-sm font-medium flex items-center gap-1">
                    Learn more <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Industry-Specific CRM Agent */}
      <section className="py-16 md:py-24 bg-muted/20">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 text-center">
            CRM AI Agent by Industry
          </h2>
          <p className="text-center text-muted-foreground mb-12">Every industry has unique CRM challenges. The NoQ5 CRM agent adapts to your specific sales process and compliance requirements.</p>
          <div className="grid md:grid-cols-2 gap-6">
            {industries.map((ind) => (
              <div key={ind.name} className="bg-card border border-border rounded-xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <ind.icon className="h-8 w-8 text-primary" />
                  <h3 className="text-xl font-semibold text-foreground">{ind.name}</h3>
                </div>
                <p className="text-muted-foreground text-sm">{ind.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deep SEO Content: The Complete Guide */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-8 text-center">
            The Complete Guide to CRM AI Agents in 2026
          </h2>
          <div className="prose prose-lg max-w-none text-muted-foreground space-y-6">
            <h3 className="text-2xl font-bold text-foreground">The Rise of Agentic CRM</h3>
            <p>
              The CRM industry is undergoing its biggest transformation since the cloud. <strong>Agentic CRM</strong> — powered by autonomous <strong>CRM AI agents</strong> — represents a fundamental shift from tools that store data to systems that act on it. Gartner predicts that by 2028, 33% of enterprise software interactions will be handled by AI agents, up from less than 1% in 2024.
            </p>
            <p>
              A <strong>CRM agent</strong> sits at the center of this revolution. Instead of forcing sales reps to be data clerks, the CRM AI agent handles all administrative work autonomously — from logging a 30-second Slack message to processing a 90-minute discovery call and updating 15 CRM fields simultaneously.
            </p>

            <h3 className="text-2xl font-bold text-foreground">How CRM Agents Process Sales Interactions</h3>
            <p>Understanding how a <strong>CRM AI agent</strong> actually works reveals why it's fundamentally different from workflow automation:</p>
            <ol className="list-decimal pl-6 space-y-3">
              <li><strong>Activity Ingestion</strong> — The agent monitors connected channels (email, calendar, calls, Slack, LinkedIn) and identifies sales-relevant interactions using NLP classification.</li>
              <li><strong>Entity Resolution</strong> — Each interaction is mapped to the correct Contact, Company, and Deal in your CRM. The agent handles duplicates, name variations, and new contacts automatically.</li>
              <li><strong>Context Extraction</strong> — Using large language models, the agent extracts structured data from unstructured conversations: deal amounts mentioned, timeline commitments, objections raised, competitors discussed, and next steps agreed.</li>
              <li><strong>Field Mapping</strong> — Extracted data is mapped to your specific CRM fields — including custom fields, picklists, and multi-select values — using your organization's schema.</li>
              <li><strong>Confidence Scoring</strong> — Each proposed update receives a confidence score. High-confidence updates (90%+) are applied automatically; lower-confidence updates are flagged for review.</li>
              <li><strong>Intelligence Generation</strong> — Beyond data entry, the agent generates pipeline forecasts, risk scores, coaching signals, and deal health metrics from the accumulated data.</li>
            </ol>

            <h3 className="text-2xl font-bold text-foreground">Key Capabilities of a Modern CRM AI Agent</h3>
            
            <h4 className="text-xl font-semibold text-foreground">Autonomous Data Capture</h4>
            <p>
              The most critical function of any <strong>CRM agent</strong> is eliminating manual data entry. NoQ5's CRM AI agent captures data from every sales touchpoint — meetings (Zoom, Google Meet, Teams), emails (Gmail, Outlook), messaging (Slack), and social (LinkedIn) — and maps each interaction to the correct contact, company, and deal in your CRM. Every captured activity includes full context: duration, participants, key topics discussed, and action items agreed.
            </p>

            <h4 className="text-xl font-semibold text-foreground">CRM Data Enrichment</h4>
            <p>
              A <strong><Link to="/crm-enrichment-agent" className="text-primary hover:underline">CRM enrichment agent</Link></strong> fills the gaps that manual research can't keep up with. NoQ5 enriches contact and company records with data from 15+ sources — including job titles, company size, industry, tech stack, recent funding, hiring signals, and social profiles. Unlike point enrichment tools like Apollo or ZoomInfo, the CRM agent enriches contextually: it prioritizes enrichment for contacts in active deal cycles and keeps records fresh as roles change.
            </p>

            <h4 className="text-xl font-semibold text-foreground">Contextual Deal Intelligence</h4>
            <p>
              Beyond data capture, an advanced CRM AI agent understands <em>what happened</em> in each interaction. It identifies buying signals ("we need to move fast on this"), objections ("budget isn't approved yet"), competitor mentions, champion identification, and next steps — then updates your CRM with this intelligence. This transforms your CRM from a static database into a live intelligence system.
            </p>

            <h4 className="text-xl font-semibold text-foreground">Predictive Revenue Forecasting</h4>
            <p>
              Traditional forecasting relies on gut feel and rep-submitted data (which is often outdated by the time it's entered). A <strong>CRM AI agent</strong> analyzes actual engagement patterns, historical close rates, and behavioral signals to generate evidence-based forecasts — with 35% greater accuracy than manual methods. Every forecast is backed by specific evidence, not just a probability number.
            </p>

            <h4 className="text-xl font-semibold text-foreground">Proactive Risk Detection</h4>
            <p>
              Your CRM agent monitors every deal for signs of trouble: declining engagement velocity, missed follow-ups, champion job changes, competitive displacement signals, and procurement delays. It alerts reps and managers before deals go dark — typically within 48 hours of the first warning sign, compared to 2-3 weeks with manual pipeline review.
            </p>

            <h3 className="text-2xl font-bold text-foreground">CRM Agent for Different Team Roles</h3>
            
            <h4 className="text-xl font-semibold text-foreground">For Sales Reps (AEs & SDRs)</h4>
            <p>
              The CRM AI agent is your personal admin assistant. It logs every call, updates every deal, creates every follow-up task, and enriches every contact — so you never touch a CRM field again. Reps using NoQ5's CRM agent reclaim 5+ hours per week of selling time. That's 250+ hours per year redirected from <Link to="/crm-data-entry-automation" className="text-primary hover:underline">CRM data entry</Link> to revenue-generating activities.
            </p>

            <h4 className="text-xl font-semibold text-foreground">For Sales Managers</h4>
            <p>
              Get real-time pipeline visibility without chasing reps for updates. The CRM agent provides deal-level health scores, coaching recommendations based on conversation analysis, and early warning alerts for at-risk deals. Run 1:1s with data, not anecdotes.
            </p>

            <h4 className="text-xl font-semibold text-foreground">For CROs & Revenue Leaders</h4>
            <p>
              Your <strong>CRM AI agent</strong> delivers <Link to="/ai-for-cros" className="text-primary hover:underline">board-ready forecasts</Link> backed by evidence, not opinions. See pipeline coverage ratios, conversion rates by stage, deal velocity trends, competitive win/loss analysis, and rep performance benchmarks — all generated autonomously from actual sales data.
            </p>

            <h4 className="text-xl font-semibold text-foreground">For Revenue Operations</h4>
            <p>
              Eliminate data quality firefighting. The CRM agent enforces field completeness, standardizes values, detects duplicates, and maintains consistent naming conventions — turning RevOps from a cleanup crew into a strategic function.
            </p>

            <h3 className="text-2xl font-bold text-foreground">CRM Agent Security & Compliance</h3>
            <p>
              Enterprise sales teams need a <strong>CRM agent</strong> they can trust with sensitive deal data. NoQ5 is built with security-first architecture:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>SOC 2 Type II certified</strong> — independently audited security controls with continuous monitoring</li>
              <li><strong>GDPR compliant</strong> — EU data residency, right to deletion, consent management, DPA available</li>
              <li><strong>Zero-trust architecture</strong> — end-to-end encryption at rest and in transit, no persistent credential storage</li>
              <li><strong>No model training on your data</strong> — your CRM data is never used to train or fine-tune AI models</li>
              <li><strong>Role-based access control</strong> — granular permissions for every team member with full audit trail</li>
              <li><strong>Data retention controls</strong> — configurable retention periods with automatic purging</li>
              <li><strong>SSO & SAML</strong> — enterprise single sign-on support</li>
            </ul>

            <h3 className="text-2xl font-bold text-foreground">CRM Agent vs. Salesforce Agentforce</h3>
            <p>
              Salesforce Agentforce is Salesforce's native agentic AI, but it's locked to the Salesforce ecosystem. It can't capture activities from Gmail, Slack, or Zoom natively, doesn't support HubSpot or Pipedrive, and requires significant configuration. NoQ5's CRM agent works across all major CRMs and communication platforms, sets up in 15 minutes, and provides deeper conversation intelligence. <Link to="/comparison/salesforce-einstein" className="text-primary hover:underline">Read the full Agentforce comparison →</Link>
            </p>

            <h3 className="text-2xl font-bold text-foreground">CRM Agent vs. HubSpot Breeze AI</h3>
            <p>
              HubSpot Breeze offers AI features within HubSpot, but its agent capabilities are limited to the HubSpot ecosystem. It lacks deep meeting transcription analysis, cross-CRM support, and the autonomous decision-making that defines a true CRM AI agent. <Link to="/comparison/hubspot" className="text-primary hover:underline">Read the full Breeze comparison →</Link>
            </p>

            <h3 className="text-2xl font-bold text-foreground">The Future of CRM: From Software to Agent</h3>
            <p>
              The trajectory is clear: CRM is evolving from a database you fill manually, to a system with automation rules, to a copilot that suggests actions, to a fully autonomous <strong>CRM AI agent</strong> that operates your entire revenue data infrastructure. Companies that adopt agentic CRM now gain a compounding advantage — cleaner data, better forecasts, faster cycles, and more selling time — that widens every quarter.
            </p>
          </div>
        </div>
      </section>

      {/* Voice-to-CRM Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              <Mic className="h-4 w-4" />
              <span>VOICE TO CRM</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Voice CRM, Built for Sales
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Every sales call carries the data your CRM needs. Most of it never gets logged. NoQ5 is voice-native: calls in, structured CRM updates out, no rep typing.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
              {
                icon: Headphones,
                title: "Capture Every Call",
                desc: "Zoom, Google Meet, Microsoft Teams, dialer of choice. Recordings stream into NoQ5 the moment the call ends.",
              },
              {
                icon: Brain,
                title: "Extract the Signal",
                desc: "MEDDIC, BANT, competitor mentions, next steps, objections, buying committee changes. Parsed automatically.",
              },
              {
                icon: Database,
                title: "Update the CRM",
                desc: "Right contact, right deal, right fields. Salesforce, HubSpot, Pipedrive, Zoho. No copy paste, no missed updates.",
              },
            ].map((item) => (
              <div key={item.title} className="bg-card p-6 rounded-xl border border-border">
                <item.icon className="h-10 w-10 text-primary mb-4" />
                <h3 className="font-bold text-lg mb-2 text-foreground">{item.title}</h3>
                <p className="text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="bg-muted/30 rounded-xl border border-border p-8">
            <h3 className="text-xl font-bold text-foreground mb-3">What "voice CRM" actually means in 2026</h3>
            <p className="text-muted-foreground mb-3">
              The category splits in three. Voice transcription tools stop at the summary. Voice agent platforms generate synthetic speech for outbound. Standalone voice CRM startups capture calls but leave you stitching the workflow together. NoQ5 is the autonomous layer that closes the loop: voice in, CRM updated, lead re-scored, forecast adjusted, brief written for the next call.
            </p>
            <p className="text-muted-foreground">
              For revenue leaders, the practical test is simple: after a 45 minute call, does the CRM reflect the conversation without a single keystroke from the rep? With NoQ5, yes.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-muted/20">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-12 text-center">
            Frequently Asked Questions About CRM Agents
          </h2>
          <div className="space-y-4">
            {[
              { q: "What is a CRM agent?", a: "A CRM agent is an AI-powered autonomous software that manages your CRM system without human intervention. It automatically logs activities, updates deal stages, enriches contact data, qualifies leads, and generates pipeline insights — acting as an intelligent chief of staff for your sales team." },
              { q: "What is a CRM AI agent?", a: "A CRM AI agent is an artificial intelligence system that autonomously operates within your CRM (Salesforce, HubSpot, Pipedrive) to automate repetitive tasks, analyze sales data, predict deal outcomes, and provide actionable recommendations using NLP, ML, and agentic AI." },
              { q: "How does a CRM agent differ from CRM automation?", a: "Traditional CRM automation follows rigid if-then rules. A CRM agent uses AI to make contextual decisions autonomously — it understands conversations, interprets sentiment, identifies buying signals, and takes the right action without pre-programmed rules." },
              { q: "What CRM fields does the AI agent update automatically?", a: "NoQ5's CRM agent updates 40+ field types including deal stage, amount, close date, next steps, contact title, engagement score, MEDDIC/BANT scores, competitor mentions, call summaries, meeting notes, and custom fields — all from conversation context." },
              { q: "Which CRMs does NoQ5's CRM AI agent work with?", a: "NoQ5 integrates natively with Salesforce, HubSpot, and Pipedrive, plus Google Workspace, Microsoft 365, Slack, Zoom, and LinkedIn for complete activity capture across all sales channels." },
              { q: "What is a CRM enrichment agent?", a: "A CRM enrichment agent automatically fills missing contact and company data — job titles, company size, tech stack, funding status, social profiles — from 15+ data sources without manual research. NoQ5 includes enrichment as part of its core CRM agent." },
              { q: "How does NoQ5 compare to Salesforce Agentforce?", a: "Agentforce only works within Salesforce, requires significant setup, and can't capture activities from external channels. NoQ5 works across Salesforce, HubSpot, and Pipedrive, captures from all communication tools, and sets up in 15 minutes." },
              { q: "Is the CRM AI agent GDPR compliant?", a: "Yes. NoQ5 is fully GDPR and SOC 2 compliant with EU data residency, end-to-end encryption, automatic data retention policies, and consent management. No customer data is used for model training." },
              { q: "How long does it take to set up a CRM agent?", a: "NoQ5's CRM agent connects to your CRM in 15 minutes via OAuth. The learning phase takes 2-3 days, supervised mode runs for another 2-3 days, and you're fully autonomous by week 2." },
              { q: "How much time does a CRM agent save?", a: "Sales reps save an average of 5+ hours per week — that's 250+ hours per year per rep redirected from CRM data entry to actual selling activities." },
              { q: "Can a CRM agent work with custom CRM fields?", a: "Yes. NoQ5's CRM agent auto-detects your CRM schema including custom fields, picklist values, and multi-select fields. It maps conversation data to your specific field structure with no developer configuration needed." },
              { q: "What's the ROI of a CRM AI agent?", a: "Companies see an average 3-5x ROI within the first quarter: 90% less manual CRM updates, 35% better forecast accuracy, 25% faster pipeline velocity, and 40% more selling time." },
              { q: "What is a voice CRM?", a: "A voice CRM captures and structures data from spoken sales conversations (calls, meetings, voice notes) and writes the result directly into CRM fields, without a rep typing anything. NoQ5's CRM agent is voice-native end to end." },
              { q: "Which companies offer the best voice AI for CRM?", a: "Voice transcription tools cover summaries. Voice agent platforms cover outbound synthesis. Standalone voice CRM startups cover capture. NoQ5 is the only autonomous CRM that takes voice from call to structured CRM updates, lead re-scoring and forecast signals in one loop." },
              { q: "How does voice AI integrate with my CRM?", a: "NoQ5 connects to Zoom, Google Meet, Microsoft Teams or your dialer, plus your CRM via OAuth. Calls are transcribed, mapped to the right contact and deal, parsed for MEDDIC, BANT and next steps, then written to the correct fields automatically." },
            ].map((faq) => (
              <div key={faq.q} className="border border-border rounded-lg p-5 bg-card">
                <h3 className="text-base font-semibold text-foreground mb-2">{faq.q}</h3>
                <p className="text-sm text-muted-foreground">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-primary/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Ready to Deploy Your CRM AI Agent?
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Teams using NoQ5's CRM agent automate their CRM, reclaim selling time, and make revenue predictable. Set up in 15 minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/trial">
              <Button size="lg" className="text-lg px-8 py-6 bg-primary hover:bg-primary/90 text-primary-foreground">
                Start Free Trial <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/blog/crm-automation-guide">
              <Button size="lg" variant="outline" className="text-lg px-8 py-6 border-border text-foreground hover:bg-accent">
                Read CRM Automation Guide
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Internal Links Hub */}
      <section className="py-12 border-t border-border">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-xl font-semibold text-foreground mb-6">CRM Agent Resources & Related Pages</h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3">By CRM Platform</h3>
              <div className="flex flex-col gap-2">
                <Link to="/ai-agent-salesforce" className="text-primary hover:underline text-sm">CRM Agent for Salesforce</Link>
                <Link to="/integrations/hubspot" className="text-primary hover:underline text-sm">CRM Agent for HubSpot</Link>
                <Link to="/integrations/pipedrive" className="text-primary hover:underline text-sm">CRM Agent for Pipedrive</Link>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3">By Capability</h3>
              <div className="flex flex-col gap-2">
                <Link to="/crm-enrichment-agent" className="text-primary hover:underline text-sm">CRM Enrichment Agent</Link>
                <Link to="/crm-data-entry-automation" className="text-primary hover:underline text-sm">CRM Data Entry Automation</Link>
                <Link to="/ai-workflow-automation" className="text-primary hover:underline text-sm">AI Workflow Automation</Link>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3">By Role</h3>
              <div className="flex flex-col gap-2">
                <Link to="/ai-for-cros" className="text-primary hover:underline text-sm">CRM Agent for CROs</Link>
                <Link to="/lead-qualification-ai" className="text-primary hover:underline text-sm">Lead Qualification AI</Link>
                <Link to="/use-cases" className="text-primary hover:underline text-sm">All Use Cases</Link>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3">Comparisons & Guides</h3>
              <div className="flex flex-col gap-2">
                <Link to="/comparison/salesforce-einstein" className="text-primary hover:underline text-sm">vs. Salesforce Agentforce</Link>
                <Link to="/comparison/hubspot" className="text-primary hover:underline text-sm">vs. HubSpot Breeze</Link>
                <Link to="/blog/crm-automation-guide" className="text-primary hover:underline text-sm">CRM Automation Guide</Link>
                <Link to="/agentic-ai-sales" className="text-primary hover:underline text-sm">Agentic AI for Sales</Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CRMAgent;
